'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * static: server render, everything visible. armed: hydrated off-screen,
 * waiting. on: scrolled into view. Put the value on the section as
 * `data-power`; `.lab-power-item` children rise in when it turns on.
 */
export type LabPower = 'static' | 'armed' | 'on'

/** Powers a lab section on as it scrolls into view. Already on screen, reduced motion or no observer: on at once. */
export function useLabPower(ref: RefObject<HTMLElement | null>): LabPower {
  const [power, setPower] = useState<LabPower>('static')

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !('IntersectionObserver' in window) || el.getBoundingClientRect().top < window.innerHeight * 0.75) {
      setPower('on')
      return
    }
    setPower('armed')
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPower('on')
          io.disconnect()
        }
      },
      { rootMargin: '0px 0px -25% 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref])

  return power
}

/** Rolls a number from the last one shown to the new one; null once it has landed. */
export function useCountUp(target: number | null, run: boolean, duration = 700): number | null {
  const [shown, setShown] = useState<number | null>(null)
  const current = useRef(0)

  useEffect(() => {
    if (!run || target === null) return
    const from = current.current
    if (from === target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      current.current = target
      setShown(null)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / duration)
      current.current = from + (target - from) * (1 - (1 - k) ** 3)
      setShown(k < 1 ? current.current : null)
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, run, duration])

  return shown
}
